import React from "react";
import { BudgetItem } from "../../types/budgeting";
import { Plus, Trash } from "lucide-react";
import { calculateTotalCost } from "../../utils/budgetingUtils";
import { StatusBadge } from "./";

interface BudgetItemProps {
  item: BudgetItem;
  index: number;
  isViewMode: boolean;
  updateItemField: (field: keyof BudgetItem, value: string) => void;
  deleteItem: () => void;
  isUpdateMode?: boolean;
  validationErrors?: {[key: string]: boolean};
  categoryIndex: number;
  totalItems: number;
  addItem: () => void;
}

const BudgetItemComponent: React.FC<BudgetItemProps> = ({
  item,
  index,
  isViewMode,
  updateItemField,
  deleteItem,
  isUpdateMode = false,
  validationErrors = {},
  categoryIndex,
  totalItems,
  addItem,
}) => {
  const isFirstItem = index === 0;
  const isOnlyItem = totalItems === 1;
  const isLastItem = index === totalItems - 1;

  const handleNumbersNeededChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (parseFloat(value) < 0) return;
    updateItemField("numbersNeeded", value);
    const total = calculateTotalCost(value, item.expectedCostPerUnit);
    updateItemField("totalCost", total);
  };

  const handleCostPerUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (parseFloat(value) < 0) return;
    updateItemField("expectedCostPerUnit", value);
    const total = calculateTotalCost(item.numbersNeeded, value);
    updateItemField("totalCost", total);
  };

  return (
    <div className="mb-4 last:mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-gray-700">Item {index + 1}</h3>
        {!isViewMode && (
          <div className="flex gap-2">
            {isLastItem && (
              <button
                onClick={addItem}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-1 w-8 h-8 flex items-center justify-center shadow-sm transition-colors"
              >
                <Plus size={18} />
              </button>
            )}
            {!isOnlyItem && (
              <button
                onClick={deleteItem}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1 w-8 h-8 flex items-center justify-center shadow-sm transition-colors"
              >
                <Trash size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Item Name*
          </label>
          <input
            id={`item-${categoryIndex}-${index}-name`}
            type="text"
            className={`w-full border ${validationErrors[`item-${categoryIndex}-${index}-name`] ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
              isViewMode ? "bg-gray-100" : ""
            }`}
            value={item.name}
            onChange={(e) => updateItemField("name", e.target.value)}
            disabled={isViewMode}
            required
          />
          {validationErrors[`item-${categoryIndex}-${index}-name`] && !isViewMode && (
            <p className="text-red-500 text-xs mt-1">Item name is required</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Numbers Needed*
          </label>
          <input
            id={`item-${categoryIndex}-${index}-numbersNeeded`}
            type="number"
            min="0"
            step="1"
            className={`w-full border ${validationErrors[`item-${categoryIndex}-${index}-numbersNeeded`] ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
              isViewMode ? "bg-gray-100" : ""
            }`}
            value={item.numbersNeeded}
            onChange={handleNumbersNeededChange}
            disabled={isViewMode}
            required
          />
          {validationErrors[`item-${categoryIndex}-${index}-numbersNeeded`] && !isViewMode && (
            <p className="text-red-500 text-xs mt-1">Numbers needed is required</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expected cost per unit*
          </label>
          <input
            id={`item-${categoryIndex}-${index}-expectedCostPerUnit`}
            type="number"
            min="0"
            step="0.01"
            className={`w-full border ${validationErrors[`item-${categoryIndex}-${index}-expectedCostPerUnit`] ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
              isViewMode ? "bg-gray-100" : ""
            }`}
            value={item.expectedCostPerUnit}
            onChange={handleCostPerUnitChange}
            disabled={isViewMode}
            required
          />
          {validationErrors[`item-${categoryIndex}-${index}-expectedCostPerUnit`] && !isViewMode && (
            <p className="text-red-500 text-xs mt-1">Cost per unit is required</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Total cost
          </label>
          <input
            type="text"
            className={`w-full border border-gray-300 rounded-md p-2 bg-gray-100`}
            value={item.totalCost}
            disabled
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description*
          </label>
          <textarea
            id={`item-${categoryIndex}-${index}-description`}
            className={`w-full border ${validationErrors[`item-${categoryIndex}-${index}-description`] ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 h-24 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
              isViewMode ? "bg-gray-100" : ""
            }`}
            value={item.description}
            onChange={(e) => updateItemField("description", e.target.value)}
            disabled={isViewMode}
            required
          ></textarea>
          {validationErrors[`item-${categoryIndex}-${index}-description`] && !isViewMode && (
            <p className="text-red-500 text-xs mt-1">Description is required</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Justification*
          </label>
          <textarea
            id={`item-${categoryIndex}-${index}-justification`}
            className={`w-full border ${validationErrors[`item-${categoryIndex}-${index}-justification`] ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 h-24 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
              isViewMode ? "bg-gray-100" : ""
            }`}
            value={item.justification}
            onChange={(e) => updateItemField("justification", e.target.value)}
            disabled={isViewMode}
            required
          ></textarea>
          {validationErrors[`item-${categoryIndex}-${index}-justification`] && !isViewMode && (
            <p className="text-red-500 text-xs mt-1">Justification is required</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {isViewMode && (item.approvalStatus === "Approved" || item.approvalStatus === "Rejected") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Approval Status
            </label>
            <div className="p-2">
              <StatusBadge status={item.approvalStatus} />
            </div>
          </div>
        )}
        
        {isUpdateMode && !isViewMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Approval Status
            </label>
            <select
              className={`w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm`}
              value={item.approvalStatus}
              onChange={(e) => updateItemField("approvalStatus", e.target.value)}
            >
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        )}
        
        {(isViewMode && item.approvalStatus === "Rejected") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Risk Mitigation Plan
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md p-2 h-24 bg-gray-100"
              value={item.riskMitigationPlan}
              disabled
            ></textarea>
          </div>
        )}
        
        {(!isViewMode && isUpdateMode && item.approvalStatus === "Rejected") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Risk Mitigation Plan*
            </label>
            <textarea
              id={`item-${categoryIndex}-${index}-riskMitigationPlan`}
              className={`w-full border ${
                validationErrors[`item-${categoryIndex}-${index}-riskMitigationPlan`] ? "border-red-500" : 
                !item.riskMitigationPlan && item.approvalStatus === "Rejected" ? "border-red-500" : "border-gray-300"
              } rounded-md p-2 h-24 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm`}
              value={item.riskMitigationPlan}
              onChange={(e) => updateItemField("riskMitigationPlan", e.target.value)}
              required={item.approvalStatus === "Rejected"}
              placeholder="Required for rejected items"
            ></textarea>
            {(validationErrors[`item-${categoryIndex}-${index}-riskMitigationPlan`] || (!item.riskMitigationPlan && item.approvalStatus === "Rejected")) && !isViewMode && (
              <p className="text-red-500 text-sm mt-1">Risk mitigation plan is required for rejected items</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetItemComponent;
